/**
 * FAQ accordion — CSS height transition, one open at a time, keyboard accessible.
 * JSON-LD FAQPage for crawlers.
 */
import { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Plus, Minus } from 'lucide-react';
import styles from './FAQ.module.css';

const FAQS = [
  { q: 'How do I know a case is real?', a: 'Every case carries a dated proof chain — verified intake, GPS-tagged field photos, signed approval, disbursement and delivery acknowledgement — all backed by an immutable, hash-chained audit trail. Open any case on the wall and follow it yourself.' },
  { q: 'Where does my money go?', a: 'Every rupee flows through the system. Donations are collected directly into a dedicated NGO account and disbursed to verified cases — sometimes paid vendor-direct to a ration shop or hospital. The transparency report shows the totals live.' },
  { q: 'How is privacy protected?', a: 'Beneficiaries are anonymous during a case and only listed publicly after it closes, with prior consent. Aadhaar is stored as a salted hash, never in plaintext. Sensitive documents are access-controlled and auto-anonymised three years after closure under the DPDP Act.' },
  { q: 'Can I request help through this site?', a: 'No — intake is staff-only and offline by design, so there is no public form to flood. If you know someone in need, reach the office directly.' },
  { q: 'Who runs USMEHAR CARE?', a: 'A registered Section 8 company, governed by family-member trustees. The founder appears once, quietly, as a patron — never on individual case cards.' },
];

export function FAQ() {
  const [open, setOpen] = useState(0);
  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: FAQS.map((f) => ({
      '@type': 'Question',
      name: f.q,
      acceptedAnswer: { '@type': 'Answer', text: f.a },
    })),
  };

  return (
    <section className="container">
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>
      <p className="eyebrow">Questions</p>
      <h2 className={styles.title}>What sceptics usually ask.</h2>
      <div className={styles.list}>
        {FAQS.map((f, i) => {
          const isOpen = open === i;
          return (
            <div key={f.q} className={`${styles.item} ${isOpen ? styles.itemOpen : ''}`}>
              <button className={styles.q} onClick={() => setOpen(isOpen ? -1 : i)} aria-expanded={isOpen}>
                <span>{f.q}</span>
                {isOpen ? <Minus size={20} /> : <Plus size={20} />}
              </button>
              <div className={styles.aWrap} style={{ gridTemplateRows: isOpen ? '1fr' : '0fr' }}>
                <div className={styles.a}>
                  <p>{f.a}</p>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}

export default FAQ;
